// Centralised date/time formatting pinned to the TENANT's configured timezone.
// The tenant zone is loaded once at auth init (see AuthContext) and applies to
// every user regardless of where they physically are — so a Mumbai agent
// supporting a UK desk sees UK time, not IST. Falls back to the browser zone if
// the tenant zone is unset/invalid. Locale is en-GB (DD/MM, 24h).

let displayTimeZone: string | undefined // undefined => browser local zone

export function setDisplayTimeZone(tz?: string | null): void {
  displayTimeZone = tz && tz.trim() ? tz.trim() : undefined
}

export function getDisplayTimeZone(): string | undefined {
  return displayTimeZone
}

const DEFAULT_DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}

function withZone(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions {
  return displayTimeZone ? { timeZone: displayTimeZone, ...options } : { ...options }
}

/** Format a UTC timestamp as date + time in the tenant's timezone (en-GB). */
export function formatDateTime(value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions): string {
  if (value === null || value === undefined || value === '') return ''
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return ''
  const opts = options ?? DEFAULT_DATETIME_OPTS
  try {
    return d.toLocaleString('en-GB', withZone(opts))
  } catch {
    // Invalid IANA zone — fall back to browser local rather than throwing.
    return d.toLocaleString('en-GB', opts)
  }
}

/** Date only (no time), in the tenant's timezone. */
export function formatDate(value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions): string {
  return formatDateTime(value, options ?? { day: 'numeric', month: 'short', year: 'numeric' })
}

/** Time only, in the tenant's timezone. */
export function formatTime(value?: string | number | Date | null, options?: Intl.DateTimeFormatOptions): string {
  return formatDateTime(value, options ?? { hour: '2-digit', minute: '2-digit' })
}
